import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Colours from '@shared/ui/atoms/Colours';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { SquareIconButton } from './LockerViewComponents';

/**
 * Destructive actions live behind the overflow rather than beside Assign and Release, so a
 * mis-tap on a phone cannot delete a locker.
 */
export function LockerRowMenu(props: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const onEdit = () => {
    setIsOpen(false);
    props.onEdit();
  };

  const onDelete = () => {
    setIsOpen(false);
    props.onDelete();
  };

  return (
    <MenuAnchor>
      <SquareIconButton type={'button'} disabled={props.disabled}
                        aria-label={t('lockers.more-actions')}
                        onClick={() => setIsOpen(!isOpen)}>
        <Icon icon={IconTypes.More} style={{ color: Colours.darkGrey }} />
      </SquareIconButton>

      {isOpen && (
        <>
          <MenuBackdrop onClick={() => setIsOpen(false)} />
          <MenuPopover>
            <MenuItem type={'button'} onClick={onEdit}>
              <Icon icon={IconTypes.Edit} style={{ color: Colours.darkGrey }} />
              <BodySmall>{t('lockers.edit-label')}</BodySmall>
            </MenuItem>
            <MenuItem type={'button'} onClick={onDelete}>
              <Icon icon={IconTypes.Bin} style={{ color: Colours.darkGrey }} />
              <BodySmall>{t('button.delete')}</BodySmall>
            </MenuItem>
          </MenuPopover>
        </>
      )}
    </MenuAnchor>
  );
}

interface Props {
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const MenuAnchor = styled.div`
  position: relative;
  flex-shrink: 0;
`

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
`

const MenuPopover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 11;
  min-width: 160px;
  background-color: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 10px;
  padding: 4px;
  box-shadow: 0 4px 16px rgba(8, 30, 34, 0.12);
`

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: ${Colours.veryLightGrey};
  }
`
